import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import {
  LayoutDashboard, ListTodo, Wallet, CalendarCheck, PalmtreeIcon,
  Radio, FolderKanban, Users, BookOpenText, LogOut, Building2, Menu, X, Tags,
} from "lucide-react";
import { useState } from "react";

const NAV = {
  engineer: [
    { to: "/", label: "Dashboard", icon: LayoutDashboard },
    { to: "/tasks", label: "My Tasks", icon: ListTodo },
    { to: "/attendance", label: "Attendance", icon: CalendarCheck },
    { to: "/expenses", label: "Expenses", icon: Wallet },
    { to: "/leaves", label: "Leaves", icon: PalmtreeIcon },
    { to: "/live-requests", label: "Live Request", icon: Radio },
  ],
  manager: [
    { to: "/", label: "Dashboard", icon: LayoutDashboard },
    { to: "/projects", label: "Projects", icon: FolderKanban },
    { to: "/tasks", label: "Tasks", icon: ListTodo },
    { to: "/approvals/expenses", label: "Expense Approvals", icon: Wallet },
    { to: "/approvals/leaves", label: "Leave Approvals", icon: PalmtreeIcon },
    { to: "/attendance", label: "Team Attendance", icon: CalendarCheck },
    { to: "/live-requests", label: "Live Requests", icon: Radio },
  ],
  admin: [
    { to: "/", label: "Dashboard", icon: LayoutDashboard },
    { to: "/projects", label: "Projects", icon: FolderKanban },
    { to: "/tasks", label: "Tasks", icon: ListTodo },
    { to: "/approvals/expenses", label: "Expense Approvals", icon: Wallet },
    { to: "/approvals/leaves", label: "Leave Approvals", icon: PalmtreeIcon },
    { to: "/attendance", label: "Attendance", icon: CalendarCheck },
    { to: "/live-requests", label: "Live Requests", icon: Radio },
    { to: "/users", label: "Users", icon: Users },
    { to: "/activity-types", label: "Activity Types", icon: Tags },
    { to: "/accounting", label: "Accounting", icon: BookOpenText },
  ],
};

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  if (!user) return null;
  const items = NAV[user.role] || NAV.engineer;

  const handleLogout = async () => {
    await logout();
    nav("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#0B0C10] text-white flex">
      {/* Sidebar (desktop) */}
      <aside className="hidden md:flex md:flex-col w-60 border-r border-white/10 bg-[#0B0C10] sticky top-0 h-screen">
        <div className="px-5 py-5 border-b border-white/10">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#FF5722] flex items-center justify-center">
              <Building2 className="w-4 h-4 text-black" />
            </div>
            <div>
              <div className="mono text-[10px] uppercase tracking-[0.2em] text-slate-500">FieldOps</div>
              <div className="text-sm font-semibold">SIPL Portal</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 py-4 overflow-y-auto">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.to === "/"}
              data-testid={`nav-${it.to.replace(/\//g, "-") || "root"}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-5 py-2.5 text-sm transition-colors border-l-2 ${
                  isActive
                    ? "border-[#FF5722] bg-[#13151A] text-white"
                    : "border-transparent text-slate-400 hover:text-white hover:bg-white/[0.03]"
                }`
              }
            >
              <it.icon className="w-4 h-4" />
              {it.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="text-xs text-slate-500 mono uppercase tracking-widest mb-1">{user.role}</div>
          <div className="text-sm font-medium truncate" data-testid="current-user-name">{user.name}</div>
          <div className="text-xs text-slate-500 truncate mono">{user.email}</div>
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="mt-3 w-full flex items-center justify-center gap-2 border border-white/15 hover:border-[#FF5722] hover:text-[#FF5722] text-slate-300 py-2 text-xs mono uppercase tracking-wider transition-colors"
          >
            <LogOut className="w-3 h-3" /> Logout
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-[#0B0C10]/95 backdrop-blur-xl border-b border-white/10">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-[#FF5722] flex items-center justify-center">
              <Building2 className="w-3.5 h-3.5 text-black" />
            </div>
            <div className="text-sm font-semibold">FieldOps</div>
          </div>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen(!mobileOpen)}
            className="p-2 border border-white/10"
          >
            {mobileOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
        </div>
        {mobileOpen && (
          <div className="border-t border-white/10 py-2 max-h-[70vh] overflow-y-auto">
            {items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.to === "/"}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-5 py-3 text-sm ${
                    isActive ? "bg-[#13151A] text-white border-l-2 border-[#FF5722]" : "text-slate-300"
                  }`
                }
              >
                <it.icon className="w-4 h-4" /> {it.label}
              </NavLink>
            ))}
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-5 py-3 text-sm text-slate-300 border-t border-white/10 mt-2"
            >
              <LogOut className="w-4 h-4" /> Logout ({user.name})
            </button>
          </div>
        )}
      </div>

      {/* Main content */}
      <main className="flex-1 min-w-0 pt-14 md:pt-0">
        <div className="px-4 md:px-8 py-6 md:py-10 max-w-[1600px]">
          {children}
        </div>
      </main>
    </div>
  );
}
